import { useAppSelector } from 'redux/hooks'
import { useNavigate } from 'react-router-dom'
import {
  PRICE_LIST_TITLE,
  PRICE_LIST_ADDING_BREADCRUMB,
} from 'texts/priceListDetails'
import { Container } from 'ui/molecules/Container'
import { PriceListDetailsForm } from '../PriceListDetailsForm'
import { FormValues } from '../PriceListDetailsForm/types'
import { isOperatorSelector, userDataSelector } from 'redux/login/selectors'
import { PriceListType } from 'api/fleet/types'
import { fleetApi } from 'api'
import { formatSeasonRange } from '../../utils/formatSeasonRange'
import { useApiRequest } from 'hooks/useApiRequest'
import { getDetailsBreadcrumbsList } from 'utils/getDetailsBreadcrumbsList'
import { URLS } from 'constants/urls'
import { getSeasonFromSeasonRanges } from '../../utils/getSeasonFromSeasonRange'
import { GTM } from 'utils/gtm'
import { DATA_LAYER_EVENT } from 'constants/dataLayerEvents'
import { prepareResponseItems } from 'utils/form'
import { useGetLocations } from '../../hooks/useGetLocations'
import { useCompanyId } from 'hooks/useCompanyId'
import { resetSelectedFilters } from 'redux/filters/slice'
import { useDispatch } from 'react-redux'
import { ACCOUNT_STATUSES, READONLY_STATUSES } from 'api/auth/constants'
import { useEffect } from 'react'
import { FormActionButtons } from 'ui/components/FormActionButtons'
import { PRICE_LIST_DETAILS_FORM_ID } from '../../constants'

export const PriceListAdd = () => {
  const isOperator = useAppSelector(isOperatorSelector)
  const navigate = useNavigate()

  const companyId = useCompanyId()
  const dispatch = useDispatch()
  const { locations, locationsLoading } = useGetLocations(String(companyId))
  const { sectionsStatuses, accountStatus } = useAppSelector(userDataSelector)
  const isAccountApproved =
    isOperator || accountStatus === ACCOUNT_STATUSES.APPROVED

  const { apiRequest, loading } = useApiRequest(
    (values) =>
      fleetApi.addPriceList({
        ...values,
        companyID: Number(companyId),
      }),
    undefined,
    true,
    undefined,
    false
  )

  const breadcrumbList = getDetailsBreadcrumbsList(
    isOperator,
    String(companyId),
    'priceLists'
  )

  useEffect(() => {
    if (
      !isAccountApproved &&
      READONLY_STATUSES.includes(sectionsStatuses?.priceListsStatus || '')
    ) {
      navigate(`${URLS.FLEET}${URLS.PRICE_LIST}`)
    }
  })

  const handleSubmit = async (data: Partial<FormValues>) => {
    const formattedSeasonRanges = data.seasonRanges
      ? formatSeasonRange(data.seasonRanges)
      : []
    const joinedSeasons = getSeasonFromSeasonRanges(formattedSeasonRanges).join(
      '_'
    )
    const values = {
      ...data,
      isActive: true,
      priceListType: data.priceListType as PriceListType,
      hirePoints:
        data.hirePoints?.map((hirePoint) => Number(hirePoint.value)) || [],
      seasonRanges: formattedSeasonRanges,
      countriesOfResidence: prepareResponseItems(
        data.countriesOfResidence || []
      ),
    }
    const response = await apiRequest(values)
    if (response) {
      dispatch(resetSelectedFilters())
      navigate(
        `${URLS.FLEET}${URLS.PRICE_LIST}/${companyId}/${response.data.priceListInfo.id}/rates`
      )
      GTM.dataLayer({
        event: DATA_LAYER_EVENT.PRICE_LIST_ADD,
        season_range: joinedSeasons,
      })
    }
  }

  const handleCancel = () =>
    navigate(`${URLS.FLEET}${URLS.PRICE_LIST}/${companyId}`)

  return (
    <Container
      breadcrumbList={breadcrumbList}
      currentPageLabel={PRICE_LIST_ADDING_BREADCRUMB}
      title={PRICE_LIST_TITLE}
      withNavigation={isOperator}
      loading={locationsLoading}
      rightBlock={
        <FormActionButtons
          handleBack={handleCancel}
          loading={loading}
          formId={PRICE_LIST_DETAILS_FORM_ID}
        />
      }
    >
      <PriceListDetailsForm
        handleSubmit={handleSubmit}
        locations={locations}
        formId={PRICE_LIST_DETAILS_FORM_ID}
      />
    </Container>
  )
}
